import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getNextQuotationNumber = createAsyncThunk(
  "quotation/getNextQuotationNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.QUOTATION.GET_NEXT_QUOTATION_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const getQuotations = createAsyncThunk(
  "quotation/getQuotations",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      const search = payload.SearchText || payload.searchText;
      if (search) params.append("searchText", search);
      const statuses =
        payload.Statuses && Array.isArray(payload.Statuses) && payload.Statuses.length > 0
          ? payload.Statuses
          : payload.Status !== undefined && payload.Status !== null && payload.Status !== ""
          ? Array.isArray(payload.Status)
            ? payload.Status
            : [payload.Status]
          : null;

      if (statuses) {
        statuses.forEach((s) => {
          if (s !== undefined && s !== null && s !== "") {
            params.append("Statuses", s);
          }
        });
      }
      if (payload.CustomerId) params.append("CustomerId", payload.CustomerId);
      if (payload.LocationId) params.append("LocationId", payload.LocationId);
      if (payload.SalesPersonId) params.append("SalesPersonId", payload.SalesPersonId);
      if (payload.StartDate) params.append("StartDate", payload.StartDate);
      if (payload.EndDate) params.append("EndDate", payload.EndDate);
      if (payload.PageNumber) params.append("PageNumber", payload.PageNumber);
      if (payload.PageSize) params.append("PageSize", payload.PageSize);
      if (payload.OrderColumn) params.append("OrderColumn", payload.OrderColumn);
      if (payload.OrderBy) params.append("OrderBy", payload.OrderBy);

      const { data } = await axios.get(`${API_URL.QUOTATION.SLUG}?${params.toString()}`);
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const getQuotationById = createAsyncThunk(
  "quotation/getQuotationById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.QUOTATION.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const getQuotationDetail = createAsyncThunk(
  "quotation/getQuotationDetail",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.QUOTATION.GET_DETAIL(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const getQuotationVersion = createAsyncThunk(
  "quotation/getQuotationVersion",
  async ({ id, versionId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.QUOTATION.GET_VERSION(id, versionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const createQuotation = createAsyncThunk(
  "quotation/createQuotation",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const updateQuotation = createAsyncThunk(
  "quotation/updateQuotation",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.QUOTATION.SLUG}/${id}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const sendQuotation = createAsyncThunk(
  "quotation/sendQuotation",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.SEND(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const acceptQuotation = createAsyncThunk(
  "quotation/acceptQuotation",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.ACCEPT(id), payload || {});
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const rejectQuotation = createAsyncThunk(
  "quotation/rejectQuotation",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.REJECT(id), payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const cancelQuotation = createAsyncThunk(
  "quotation/cancelQuotation",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.CANCEL(id), payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const reviseQuotation = createAsyncThunk(
  "quotation/reviseQuotation",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.QUOTATION.REVISE(id), payload || {});
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const deleteQuotation = createAsyncThunk(
  "quotation/deleteQuotation",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.QUOTATION.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const getQuotationSoPreview = createAsyncThunk(
  "quotation/getQuotationSoPreview",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.QUOTATION.SO_PREVIEW(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data || error.message);
    }
  }
);

export const resolveQuotationItemsFromFile = createAsyncThunk(
  "quotation/resolveQuotationItemsFromFile",
  async (
    { file, customerId, sourceOfSupplyStateId, destinationOfSupplyStateId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (customerId) params.append("customerId", customerId);
      if (sourceOfSupplyStateId)
        params.append("sourceOfSupplyStateId", sourceOfSupplyStateId);
      if (destinationOfSupplyStateId)
        params.append("destinationOfSupplyStateId", destinationOfSupplyStateId);

      const response = await axios.post(
        `${API_URL.QUOTATION.RESOLVE_ITEMS_FROM_FILE}?${params.toString()}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000,
        },
      );
      return response.data?.data;
    } catch (error) {
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
        });
      }
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

const initialState = {
  loading: false,
  error: null,
  quotations: [],
  totalCount: 0,
  active_payload: null,
  selectedQuotation: null,
  selectedQuotationDetail: null,
  selectedVersionDetail: null,
  soPreviewData: null,
  nextQuotationNumber: null,
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    clearSelectedQuotation: (state) => {
      state.selectedQuotation = null;
      state.selectedQuotationDetail = null;
      state.selectedVersionDetail = null;
      state.soPreviewData = null;
    },
    clearSoPreviewData: (state) => {
      state.soPreviewData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getQuotations
      .addCase(getQuotations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload?.list || [];
        state.totalCount = action.payload?.data?.totalRecords || 0;
        state.active_payload = action.meta.arg;
      })
      .addCase(getQuotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getQuotationById
      .addCase(getQuotationById.fulfilled, (state, action) => {
        state.selectedQuotation = action.payload;
      })
      // getQuotationDetail
      .addCase(getQuotationDetail.fulfilled, (state, action) => {
        state.selectedQuotationDetail = action.payload;
      })
      // getQuotationVersion
      .addCase(getQuotationVersion.fulfilled, (state, action) => {
        state.selectedVersionDetail = action.payload;
      })
      // getNextQuotationNumber
      .addCase(getNextQuotationNumber.fulfilled, (state, action) => {
        state.nextQuotationNumber = action.payload;
      })
      // getQuotationSoPreview
      .addCase(getQuotationSoPreview.fulfilled, (state, action) => {
        state.soPreviewData = action.payload;
      });
  },
});

export const { clearSelectedQuotation, clearSoPreviewData } = quotationSlice.actions;
export default quotationSlice.reducer;
